import * as React from 'react';
import { useParams } from 'react-router';

import { Permission } from '@admin/components/helpers/permission';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { NotesPanel } from '@admin/components/notes/panel';
import { Spinner } from '@admin/components/spinner';
import { TicketsPanel } from '@admin/components/tickets/panel';
import { useTitle } from '@admin/hooks/use_title';
import { CLAIMS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { Claim__Status, History__Subject, NoteNotableEnum, Ticket__TicketableEnum, useClaimQuery } from '@admin/schema';

import { Attributes } from './attributes';
import { OfferHistory } from './offer_history';
import { canManageClaims } from './permissions';
import { Specifications } from './specifications';

const EDITABLE_STATES = [Claim__Status.InReview, Claim__Status.Approved];

const DetailsContent: React.FC = () => {
  useTitle('Claim');
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useClaimQuery({ client, variables: { id } });

  if (error) {
    return <div>There was an error loading this claim: {error.message}</div>;
  } else if (loading) {
    return <Spinner />;
  } else if (!data) {
    return <div>Claim not found</div>;
  }

  const { claim } = data;

  const editable = EDITABLE_STATES.includes(claim.status) && canManageClaims();

  return (
    <>
      <Attributes editable={editable} claim={claim} />
      <Specifications claim={claim} editable={editable} />
      <OfferHistory offers={claim.offers} />
      <TicketsPanel ticketable={{ id: claim.id, type: Ticket__TicketableEnum.Claim }} />
      <NotesPanel type={NoteNotableEnum.Claim} id={claim.id} />
      <HistoriesPanel id={claim.id} type={History__Subject.Claim} />
    </>
  );
};

export const Details: React.FC = () => (
  <Permission resource={CLAIMS.resource} action={CLAIMS.actions.read}>
    <DetailsContent />
  </Permission>
);
